import { isEqual } from 'lodash';
import { memo } from 'react';
import { useSelector } from 'react-redux';

import {
  type ContentCollectionClipWithUpload,
  contentCollectionApplyAllSelector,
  contentCollectionFormIsInvalidSelector,
} from '@air/feature-content-collection/redux';
import { useBreakpointsContext } from '@air/provider-media-query';

import { ContentCollectionGalleryAssetCard } from '~/components/ContentCollection/ContentCollectionGalleryAssetCard';
import { ContentCollectionGalleryAssetCustomFields } from '~/components/ContentCollection/ContentCollectionGalleryAssetCustomFields';
import { ContentCollectionGalleryAssetDeleteButton } from '~/components/ContentCollection/ContentCollectionGalleryAssetDeleteButton';
import { ContentCollectionGalleryAssetDescription } from '~/components/ContentCollection/ContentCollectionGalleryAssetDescription';
import { ContentCollectionGalleryAssetMenu } from '~/components/ContentCollection/ContentCollectionGalleryAssetMenu';
import { ContentCollectionGalleryAssetStatus } from '~/components/ContentCollection/ContentCollectionGalleryAssetStatus';
import { ContentCollectionGalleryAssetThumbnail } from '~/components/ContentCollection/ContentCollectionGalleryAssetThumbnail';
import { ContentCollectionGalleryAssetTitle } from '~/components/ContentCollection/ContentCollectionGalleryAssetTitle';
import { useGetContentCollectionGalleryAssetMenuOptions } from '~/components/ContentCollection/hooks/useGetContentCollectionGalleryAssetMenuOptions';
import { GalleryItemType, type GalleryViewRender } from '~/components/Gallery/types';
import { type AirState } from '~/store/types';

export type ContentCollectionGalleryAssetProps = GalleryViewRender<ContentCollectionClipWithUpload>;

export const ContentCollectionGalleryAsset = memo(({ data: clip }: ContentCollectionGalleryAssetProps) => {
  const { isAboveMediumScreen } = useBreakpointsContext();
  const showInvalidState = useSelector((st: AirState) => contentCollectionFormIsInvalidSelector(st, [clip.id]));
  const applyAll = useSelector(contentCollectionApplyAllSelector);

  const { getContentCollectionAssetMenuOptions } = useGetContentCollectionGalleryAssetMenuOptions({ clip });

  return (
    <ContentCollectionGalleryAssetCard
      getItemMenuOptions={getContentCollectionAssetMenuOptions}
      getSelectionMenuOptions={getContentCollectionAssetMenuOptions}
      isInvalid={showInvalidState}
      isSelectable
      item={{
        id: clip.id,
        item: clip,
        type: GalleryItemType.asset,
      }}
    >
      {({ isHovering, setIsMenuOpen }) => (
        <>
          <div className="pointer-events-none relative flex h-[185px] shrink-0 items-center justify-center overflow-hidden rounded-4 ads:bg-subtle bg-gray-4">
            <ContentCollectionGalleryAssetThumbnail clip={clip} />
            <div className="absolute inset-x-1 top-1 flex items-center justify-between gap-2">
              <div className="py-0.5">
                <ContentCollectionGalleryAssetStatus clip={clip} isHovering={isHovering} />
              </div>
              {(isHovering || !isAboveMediumScreen) && (
                <div className="pointer-events-auto flex items-center gap-2">
                  <ContentCollectionGalleryAssetDeleteButton clip={clip} />
                  {!applyAll && <ContentCollectionGalleryAssetMenu clip={clip} setIsMenuOpen={setIsMenuOpen} />}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col">
            <ContentCollectionGalleryAssetTitle clip={clip} />
            <ContentCollectionGalleryAssetDescription clip={clip} />
            {!applyAll && <ContentCollectionGalleryAssetCustomFields clip={clip} />}
          </div>
        </>
      )}
    </ContentCollectionGalleryAssetCard>
  );
}, isEqual);

ContentCollectionGalleryAsset.displayName = 'ContentCollectionGalleryAsset';
